import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import PageViewTracker from '@/components/gtm/PageViewTracker'
import YouthEnvironmentGuard from '@/components/tracking/YouthEnvironmentGuard'
import PrivateRoutes from './PrivateRoutes'
import RoleRoutes from './RoleRoutes'
import {
  renderTestRoutes,
  renderStudentDashboardTestRoutes,
  areTestRoutesEnabled
} from './testRoutes'
import UiTestNavigator from '@/components/test/UiTestNavigator'
import { ROLES } from '@/config/roles'
import RoleRedirectHandler from '@/components/auth/RoleRedirectHandler'

import AccessibilityStatementPage from '@/pages/accessibility-statement/AccessibilityStatementPage'

import ChatbotController from './ChatbotController'
// These load immediately as they're needed for initial render
import HomePageLayout from '../layout/home/HomePageLayout'
import LandingPage from '../pages/home/landing/NewLandingPage'
// import StayConnect from '@/pages/landing/newCompoenent/StayConnect'

//Lazy imports
const StayConnect = lazy(
  () => import('@/pages/home/landing/newCompoenent/StayConnect')
)
// Auth Pages
const LoginPage = lazy(() => import('@/pages/auth/login/Login'))
const RegisterPage = lazy(() => import('@/pages/auth/register/Register'))
const VerificationNotePage = lazy(
  () => import('@/pages/auth/register/VerificationNotePage')
)
const ForgotPassword = lazy(() => import('@/pages/auth/login/ForgotPassword'))
const VerifyPasswordResetOTP = lazy(
  () => import('@/pages/auth/login/VerifyPasswordResetOTP')
)
const ResetPasswordPage = lazy(
  () => import('@/pages/auth/reset-password/ResetPasswordPage')
)
const EmailVerificationPage = lazy(
  () => import('@/pages/auth/verify-email/EmailVerificationPage')
)
const RoleSelectPage = lazy(() => import('../pages/auth/roleSelect/RoleSelect'))

// Landing/Public Pages
const TermsOfServicePage = lazy(
  () => import('@/pages/terms-of-service/TermsOfServicePage')
)
const PrivacyPolicyPage = lazy(
  () => import('@/pages/privacy-policy/PrivacyPolicyPage')
)
const ChildPrivacyPolicyPage = lazy(
  () => import('@/pages/privacy-policy/childP-privacy/ChildPrivacyPolicyPage')
)
const CookiePolicyPage = lazy(
  () => import('@/pages/cookie-policy/CookiePolicyPage')
)
const DataRetentionPolicyPage = lazy(
  () => import('@/pages/data-retention-policy/DataRetentionPolicyPage')
)
const ContactUsPage = lazy(() => import('../pages/contact/ContactUsPage'))
const EducatorLandingPage = lazy(() => import('@/pages/home/educator/Educator'))
const Pricing = lazy(() => import('@/pages/home/pricing/Pricing'))
const ArticleDetailPage = lazy(
  () => import('../pages/home/article/ArticleDetailPage')
)

const BlogListPage = lazy(() => import('@/pages/blog/BlogListPage'))
const BlogPostPage = lazy(() => import('@/pages/blog/BlogPostPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

// Student Access
const StudentAccessPage = lazy(
  () => import('../pages/student-access/StudentAccessPage')
)
const StudentRegistration = lazy(
  () => import('../components/student-access/StudentRegistration')
)

// Plan Options Pages
const SchoolPlanOptionsPage = lazy(
  () => import('@/pages/plan-options/SchoolPlanOptionsPage')
)

// Layouts (lazy loaded as they contain heavy components)
const StudentDashboardLayout = lazy(
  () => import('../layout/dashboard/student/StudentDashboardLayout')
)
const TeacherDashboardLayout = lazy(
  () => import('../layout/dashboard/teacher/TeacherDashboardLayout')
)
const ChildDashboardLayout = lazy(
  () => import('../layout/dashboard/child/ChildDashboardLayout')
)
const AdminDashboardLayout = lazy(
  () => import('../layout/dashboard/admin/AdminDashboardLayout')
)
const ParentDashboardLayout = lazy(
  () => import('@/layout/dashboard/parent/ParentDashboardLayout')
)
const EducatorDashboardLayout = lazy(
  () => import('@/layout/dashboard/educator/EducatorDashboardLayout')
)
const OnboardingLayout = lazy(
  () => import('../layout/onboarding/OnboardingLayout')
)
const SchoolPortalSetupLayout = lazy(
  () =>
    import('../layout/onboarding/school/school-portal-setup/SchoolPortalSetupLayout')
)

// Teacher Dashboard Pages
const TeacherClassroomPage = lazy(
  () => import('../pages/dashboard/teacher/TeacherClassroomPage')
)
const TeacherAccessCodePage = lazy(
  () => import('../pages/dashboard/teacher/TeacherAccessCodePage')
)
const TeacherPlannerPage = lazy(
  () => import('../pages/dashboard/teacher/TeacherPlannerPage')
)
const TeacherAssignmentsPage = lazy(
  () => import('../pages/dashboard/teacher/TeacherAssignmentsPage')
)
const TeacherQuizzesPage = lazy(
  () => import('../pages/dashboard/teacher/TeacherQuizzesPage')
)
const TeacherResourcePage = lazy(
  () => import('../pages/dashboard/teacher/TeacherResourcePage.jsx')
)
const TeacherAnalyticsPage = lazy(
  () => import('../pages/dashboard/teacher/TeacherAnalyticsPage.jsx')
)
const TeacherCommunityPage = lazy(
  () => import('../pages/dashboard/teacher/TeacherCommunityPage.jsx')
)
const TeacherProfilePage = lazy(
  () => import('../pages/dashboard/teacher/TeacherProfilePage.jsx')
)
const TeacherHelpPage = lazy(
  () => import('../pages/dashboard/teacher/TeacherHelpPage')
)
const TeacherContactPage = lazy(
  () => import('@/pages/dashboard/teacher/TeacherContactPage')
)

// Student Dashboard Pages
const StudentMissionPage = lazy(
  () => import('../pages/dashboard/student/StudentMissionPage')
)
const StudentQuizzesPage = lazy(
  () => import('../pages/dashboard/student/StudentQuizzesPage')
)
const StudentHelpPage = lazy(
  () => import('../pages/dashboard/student/StudentHelpPage')
)
const StudentContactPage = lazy(
  () => import('../pages/dashboard/student/StudentContactPage')
)
const StudentQuizPage = lazy(
  () => import('../pages/dashboard/student/StudentQuizPage')
)
const StudentNotesPage = lazy(
  () => import('../pages/dashboard/student/StudentNotesPage')
)
const StudentSupportChatPage = lazy(
  () => import('../pages/dashboard/student/StudentSupportChatPage')
)
const StudyGuidePage = lazy(
  () => import('../pages/dashboard/student/StudyGuidePage')
)
const StudentProfilePage = lazy(
  () => import('../pages/dashboard/student/StudentProfilePage')
)
const StudentMyProtectionPlan = lazy(
  () => import('../pages/dashboard/student/StudentMyProtectionPlan')
)

// Child Dashboard Pages
const ChildMissionPage = lazy(
  () => import('../pages/dashboard/child/ChildMissionPage')
)
const ChildAssignmentsPage = lazy(
  () => import('../pages/dashboard/child/ChildAssignmentsPage')
)
const ChildProfilePage = lazy(
  () => import('../pages/dashboard/child/ChildProfilePage')
)
const ChildSupportChatPage = lazy(
  () => import('../pages/dashboard/child/ChildSupportChatPage')
)
const ChildHelpPage = lazy(
  () => import('../pages/dashboard/child/ChildHelpPage')
)
const ChildQuizPage = lazy(
  () => import('@/pages/dashboard/child/ChildQuizPage')
)
const ChildNotesPage = lazy(
  () => import('@/pages/dashboard/child/ChildNotesPage')
)
const ChildMyProtectionPlanPlage = lazy(
  () => import('../pages/dashboard/child/ChildMyProtectionPlanPlage')
)
const ChildAchievements = lazy(
  () => import('@/components/dashboard/child/achivements/ChildAchievements')
)
const ChildLeaderboardPage = lazy(
  () => import('@/pages/dashboard/child/ChildLeaderboardPage')
)
const ChildDigiShopPage = lazy(
  () => import('@/pages/dashboard/child/ChildDigiShopPage')
)
const StudentAchievements = lazy(
  () => import('@/components/dashboard/student/achivements/StudentAchievements')
)

// Parent Dashboard Pages
const ParentDashboardPage = lazy(
  () => import('../pages/dashboard/parent/ParentDashboardPage')
)
const ParentSelectProfilePage = lazy(
  () => import('@/pages/dashboard/parent/ParentSelectProfilePage')
)
const ParentMyProfilePage = lazy(
  () => import('@/pages/dashboard/parent/ParentMyProfilePage')
)
const ParentHelpPage = lazy(
  () => import('@/pages/dashboard/parent/ParentHelpPage')
)
const ParentBillingPage = lazy(
  () => import('@/pages/dashboard/parent/ParentBillingPage')
)
const ParentResourcePage = lazy(
  () => import('@/pages/dashboard/parent/ParentResourcePage.jsx')
)
const ParentContactPage = lazy(
  () => import('@/pages/dashboard/parent/ParentContactPage')
)
const ParentEmptyPage = lazy(
  () => import('@/pages/dashboard/parent/ParentEmptyPage')
)
const AddAnotherChild = lazy(
  () => import('@/components/dashboard/parent/AddAnotherCild')
)
const FamilyTalksPage = lazy(
  () => import('@/pages/dashboard/parent/FamilyTalksPage')
)
const ManageChildLesson = lazy(
  () => import('@/components/dashboard/parent/manageLessons/ManageChildLesson')
)
const Achivements = lazy(
  () => import('@/components/dashboard/parent/achivements/Achivements')
)
const ImpactReport = lazy(
  () => import('@/components/dashboard/parent/impactReport/ImpactReport')
)
const ParentOnboarding = lazy(
  () => import('@/components/onboarding/parent/ParentOnboarding')
)

// Admin Dashboard Pages
const QuizBuilderPage = lazy(
  () => import('../pages/dashboard/admin/QuizBuilderPage')
)
const AllQuizzesPage = lazy(
  () => import('../pages/dashboard/admin/AllQuizzesPage')
)
const ProtectionPlanPage = lazy(
  () => import('../pages/dashboard/admin/ProtectionPlanPage')
)
const MaintenancePage = lazy(
  () => import('../pages/dashboard/admin/MaintenancePage')
)
const AuditLogPage = lazy(() => import('@/pages/dashboard/admin/AuditLogPage'))
const AdminClassDetailPage = lazy(
  () => import('@/pages/dashboard/admin/classes/AdminClassDetailPage')
)
const NotificationTestPage = lazy(
  () => import('@/pages/dashboard/admin/NotificationTestPage')
)
const CloudFunctionsTestIndex = lazy(
  () => import('@/pages/dashboard/admin/cloud-functions/CloudFunctionsTestIndex')
)
const CloudFunctionTestPage = lazy(
  () => import('@/pages/dashboard/admin/cloud-functions/CloudFunctionTestPage')
)
const WebhooksPage = lazy(
  () => import('@/pages/dashboard/admin/webhooks/WebhooksPage')
)
const AdminOverviewPage = lazy(
  () => import('@/pages/dashboard/admin/AdminOverviewPage')
)
const AdminProfilePage = lazy(
  () => import('@/pages/dashboard/admin/AdminProfilePage')
)
const LawEnforcementReportPage = lazy(
  () => import('@/pages/dashboard/admin/LawEnforcementReportPage')
)
const AnnouncementManagementPage = lazy(
  () => import('@/pages/dashboard/admin/AnnouncementManagementPage')
)
const OurBlogsPage = lazy(() => import('@/pages/dashboard/admin/OurBlogsPage'))
const FindUserPage = lazy(() => import('@/pages/dashboard/admin/FindUserPage'))
const SchoolUser = lazy(
  () => import('@/pages/dashboard/admin/user_accounts/school_users/SchoolUser')
)
const AllUser = lazy(
  () => import('@/pages/dashboard/admin/user_accounts/all_users/AllUser')
)
const ChildrenAccounts = lazy(
  () =>
    import('@/pages/dashboard/admin/user_accounts/children/ChildrenAccounts')
)
const UserAnalytics = lazy(
  () => import('@/pages/dashboard/admin/analytics/UserAnalytics')
)
const LessonAnalytics = lazy(
  () => import('@/pages/dashboard/admin/analytics/LessonAnalytics')
)
const MissionAttemptAnalyticsPage = lazy(
  () => import('@/pages/dashboard/admin/missions/MissionAttemptAnalyticsPage')
)
const MissionAttemptAnalyticsDetailPage = lazy(
  () =>
    import('@/pages/dashboard/admin/missions/MissionAttemptAnalyticsDetailPage')
)
const MissionLoadingLogsPage = lazy(
  () => import('@/pages/dashboard/admin/missions/MissionLoadingLogsPage')
)
const MissionLoadingLogDetailPage = lazy(
  () => import('@/pages/dashboard/admin/missions/MissionLoadingLogDetailPage')
)
const ConfidenceTestAnalytics = lazy(
  () => import('@/pages/dashboard/admin/analytics/ConfidenceTestAnalytics')
)
const BuyingJourneyAnalytics = lazy(
  () => import('@/pages/dashboard/admin/analytics/BuyingJourneyAnalytics')
)
const GoogleAnalyticsPage = lazy(
  () => import('@/pages/dashboard/admin/integrations/GoogleAnalyticsPage')
)
const SentryTest = lazy(() => import('@/pages/dashboard/admin/SentryTest'))
const StripeHealth = lazy(() => import('@/pages/dashboard/admin/StripeHealth'))
const Tickets = lazy(() => import('@/pages/dashboard/admin/tickets/Tickets'))
const TicketDetail = lazy(
  () => import('@/pages/dashboard/admin/tickets/TicketDetail')
)
const Feedbacks = lazy(
  () => import('@/pages/dashboard/admin/feedbacks/Feedbacks')
)
const SubscriptionsPage = lazy(
  () => import('@/pages/dashboard/admin/subscriptions/SubscriptionsPage')
)

// Educator Dashboard Pages
const EducatorDashboardPage = lazy(
  () => import('@/pages/dashboard/educator/EducatorDashboardPage')
)
const EducatorProfilePage = lazy(
  () => import('@/pages/dashboard/educator/EducatorProfilePage')
)
const EducatorSchoolDetailsPage = lazy(
  () => import('@/pages/dashboard/educator/EducatorSchoolDetailsPage')
)
const EducatorBillingPage = lazy(
  () => import('@/pages/dashboard/educator/EducatorBillingPage')
)
const EducatoreLessonPage = lazy(
  () => import('@/pages/dashboard/educator/EducatoreLessonPage')
)
const EducatorHelpPage = lazy(
  () => import('@/pages/dashboard/educator/EducatorHelpPage')
)
const EducatorContactPage = lazy(
  () => import('@/pages/dashboard/educator/EducatorContactPage')
)
const EducatorClassroomPage = lazy(
  () => import('@/pages/dashboard/educator/EducatorClassroomPage')
)
const SchoolUsers = lazy(
  () => import('@/components/dashboard/educator/users/SchoolUsers')
)
const EducatorAccountSetup = lazy(
  () => import('@/pages/auth/accountSetup/EducatorAccountSetup')
)
const StudentLeaderboardPage = lazy(
  () => import('@/pages/dashboard/student/LeaderBoardPage')
)
const StudentDigiShopPage = lazy(
  () => import('@/pages/dashboard/student/DigiShopPage')
)
// Onboarding Pages
const SchoolPortalSetupPage = lazy(
  () =>
    import('../pages/onboarding/school/school-portal-setup/SchoolPortalSetupPage')
)
const TeacherAccountSetupPage = lazy(
  () =>
    import('../pages/onboarding/school/teacher-account-setup/TeacherAccountSetupPage')
)
const AdminAccountSetupPage = lazy(
  () =>
    import('../pages/onboarding/school/admin-account-setup/AdminAccountSetupPage')
)
const StudentAccountSetupPage = lazy(
  () =>
    import('@/pages/onboarding/school/student-account-setup/StudentAccountSetupPage')
)
const MinorPlanOptions = lazy(
  () => import('../pages/onboarding/minor/MinorPlanOptions')
)
const PaymentPage = lazy(() => import('@/pages/payment/PaymentPage'))
const PaymentSuccessPage = lazy(
  () => import('@/pages/payment/PaymentSuccessPage')
)
const SuccessPayment = lazy(() => import('@/pages/payment/SuccessPayment'))
const ChildAccountTypePage = lazy(
  () => import('../pages/onboarding/parent/ChildAccountTypePage')
)
const CreateChildAccountPage = lazy(
  () => import('../pages/onboarding/parent/CreateChildAccountPage')
)
const FamilyPlanSetupPage = lazy(
  () => import('../pages/onboarding/parent/FamilyPlanSetupPage')
)
const ParentSetupSummaryPage = lazy(
  () => import('../pages/onboarding/parent/ParentSetupSummaryPage')
)
const EditProfilesPage = lazy(
  () => import('../pages/onboarding/parent/EditProfilesPage')
)
const ChildAccountSetup = lazy(
  () => import('@/components/onboarding/parent/ChildAccountSetup')
)

// Game Pages (Heavy - should definitely be lazy loaded)
const MissionGamePage = lazy(
  () => import('../pages/game/mission/MissionGamePage.jsx')
)
const CarTest = lazy(() => import('@/pages/game/miniGame/carRace/carTest'))
const PacmanGamePage = lazy(() => import('@/pages/game/miniGame/pacman/Pacman'))
const IpAddressGame = lazy(
  () => import('@/pages/game/miniGame/ipAddress/IpAddressGame')
)
const DigitalFootprintPage = lazy(
  () => import('@/pages/game/miniGame/digitalFootprint/DigitalFootprint')
)
const PasswordInspectorGame = lazy(
  () => import('@/pages/game/miniGame/passwordInsperctor/PasswordInspector')
)
const BugScreamGame = lazy(
  () =>
    import('@/components/game/mission/scene_cmp/action_cmp/bugScream/BugScream')
)
const WebCam = lazy(
  () => import('@/components/game/mission/scene_cmp/action_cmp/webcam/WebCam')
)
const InstructionVideoTest = lazy(
  () => import('@/pages/game/test/InstructionVideoTest')
)

// Misc
const KlaviyoTest = lazy(() => import('@/pages/klaviyoTest/klaviyoTest'))
const AboutUs = lazy(() => import('@/pages/home/about-us/AboutUs'))
// ============================================
// SUSPENSE WRAPPER COMPONENT
// ============================================
const SuspenseWrapper = ({ children }) => <Suspense>{children}</Suspense>

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <YouthEnvironmentGuard />
      <PageViewTracker />
      <RoleRedirectHandler />
      <ChatbotController />
      {areTestRoutesEnabled() ? <UiTestNavigator /> : null}
      <SuspenseWrapper>
        <Routes>
          {/* Public routes */}
          <Route path='/stay-connect' element={<StayConnect />} />
          {/* <Route path='/lp-hero-section' element={<LpHeroSection />} /> */}
          {/* Landing Page Route */}
          <Route path='/' element={<HomePageLayout />}>
            <Route index element={<LandingPage />} />
            <Route
              path='/terms-of-service-en'
              element={<TermsOfServicePage />}
            />
            <Route path='/privacy-policy-en' element={<PrivacyPolicyPage />} />
            <Route
              path='/children-privacy-policy-en'
              element={<ChildPrivacyPolicyPage />}
            />
            <Route path='/cookie-policy-en' element={<CookiePolicyPage />} />
            <Route
              path='/data-retention-policy-en'
              element={<DataRetentionPolicyPage />}
            />
            <Route path='/contact-us' element={<ContactUsPage />} />
            <Route path='/educator-landing' element={<EducatorLandingPage />} />
            <Route path='/pricing' element={<Pricing />} />
            <Route
              path='/accessibility'
              element={<AccessibilityStatementPage />}
            />
            <Route path='/blog' element={<BlogListPage />} />
            <Route path='/blog/:slug' element={<BlogPostPage />} />
            <Route path='/about-us' element={<AboutUs />} />
          </Route>

          {/* Auth Routes */}
          <Route path='/login' element={<LoginPage />} />
          <Route path='/register' element={<RegisterPage />} />
          <Route path='/forgot-password' element={<ForgotPassword />} />
          <Route
            path='/auth/verify-password-reset-otp'
            element={<VerifyPasswordResetOTP />}
          />
          <Route path='/cookie-policy-en' element={<CookiePolicyPage />} />
          <Route
            path='/data-retention-policy-en'
            element={<DataRetentionPolicyPage />}
          />

          <Route path='/contact-us' element={<ContactUsPage />} />
          <Route path='/educator-landing' element={<EducatorLandingPage />} />
          <Route path='/pricing' element={<Pricing />} />
          <Route path='/reset-password' element={<ResetPasswordPage />} />
          <Route path='/student-access' element={<StudentAccessPage />} />
          <Route
            path='/student-access/registration'
            element={<StudentRegistration />}
          />

          {/* Article Routes */}
          <Route path='/articles' element={<Navigate to='/' replace />} />
          {/* <Route path='/article/:articleId' element={<ArticleDetailPage />} /> */}
          <Route path='/faqs' element={<Navigate to='/' replace />} />

          {/* Blog Routes */}

          {/* <Route path='/educator-access' element={<EducatorAccessPage />} /> */}

          {/* testing routes (Digipalz admin only; staging/dev) */}
          {renderTestRoutes()}
          {renderStudentDashboardTestRoutes()}
          <Route path='/verify-email' element={<EmailVerificationPage />} />
          {/* Private routes that require authentication */}
          <Route element={<PrivateRoutes />}>
            <Route
              path='/register/verify-email'
              element={<VerificationNotePage />}
            />
            <Route element={<SchoolPortalSetupLayout />}>
              <Route
                path='/educator-access/school-onboarding'
                element={<SchoolPortalSetupPage />}
              />
              <Route
                path='/educator-access/teacher-account-setup'
                element={<TeacherAccountSetupPage />}
              />
              <Route
                path='/educator-access/student-account-setup'
                element={<StudentAccountSetupPage />}
              />
            </Route>
            <Route path='/auth/role-select' element={<RoleSelectPage />} />
            {/* Admin Dashboard Routes */}
            <Route element={<RoleRoutes allowedRoles={[ROLES.ADMIN]} />}>
              <Route path='/dashboard/admin' element={<AdminDashboardLayout />}>
                <Route index element={<AdminOverviewPage />} />
                <Route path='/dashboard/admin/tickets' element={<Tickets />} />
                <Route
                  path='/dashboard/admin/tickets/:ticketId'
                  element={<TicketDetail />}
                />
                <Route
                  path='/dashboard/admin/feedbacks'
                  element={<Feedbacks />}
                />
                <Route
                  path='/dashboard/admin/all-quizzes'
                  element={<AllQuizzesPage />}
                />
                {/* User Accounts Routes */}
                <Route
                  path='/dashboard/admin/user-accounts/all-users'
                  element={<AllUser />}
                />
                <Route
                  path='/dashboard/admin/user-accounts/parents'
                  element={
                    <AllUser
                      key='user-accounts-parents'
                      title='Parents'
                      subtitle='View and manage parent accounts'
                      roleScope='PARENT'
                    />
                  }
                />
                <Route
                  path='/dashboard/admin/user-accounts/teachers'
                  element={
                    <AllUser
                      key='user-accounts-teachers'
                      title='Teachers'
                      subtitle='View and manage teacher accounts'
                      roleScope='TEACHER'
                    />
                  }
                />
                <Route
                  path='/dashboard/admin/user-accounts/students'
                  element={
                    <AllUser
                      key='user-accounts-students'
                      title='Students'
                      subtitle='View and manage student accounts'
                      roleScope='STUDENT'
                    />
                  }
                />
                <Route
                  path='/dashboard/admin/user-accounts/children'
                  element={<ChildrenAccounts />}
                />
                <Route
                  path='/dashboard/admin/user-accounts/schools'
                  element={<SchoolUser />}
                />
                <Route
                  path='/dashboard/admin/find-user'
                  element={<FindUserPage />}
                />
                <Route
                  path='/dashboard/admin/subscriptions'
                  element={<SubscriptionsPage />}
                />
                {/* Analytics Routes */}
                <Route
                  index
                  path='/dashboard/admin/analytics/user-analytics'
                  element={<UserAnalytics />}
                />
                <Route
                  path='/dashboard/admin/analytics/lesson-analytics'
                  element={<LessonAnalytics />}
                />
                <Route
                  path='/dashboard/admin/analytics/confidence-test'
                  element={<ConfidenceTestAnalytics />}
                />
                <Route
                  path='/dashboard/admin/analytics/buying-journey'
                  element={<BuyingJourneyAnalytics />}
                />
                <Route
                  path='/dashboard/admin/missions/analytics'
                  element={<MissionAttemptAnalyticsPage />}
                />
                <Route
                  path='/dashboard/admin/missions/analytics/:missionId'
                  element={<MissionAttemptAnalyticsDetailPage />}
                />
                <Route
                  path='/dashboard/admin/missions/loading-logs'
                  element={<MissionLoadingLogsPage />}
                />
                <Route
                  path='/dashboard/admin/missions/loading-logs/:missionId'
                  element={<MissionLoadingLogDetailPage />}
                />
                <Route
                  path='/dashboard/admin/quiz-builder'
                  element={<QuizBuilderPage />}
                />
                <Route
                  path='/dashboard/admin/quiz-builder/:quizId'
                  element={<QuizBuilderPage />}
                />
                <Route
                  path='/dashboard/admin/protect-plan'
                  element={<ProtectionPlanPage />}
                />
                <Route
                  path='/dashboard/admin/integrations/sentry'
                  element={<SentryTest />}
                />
                <Route
                  path='/dashboard/admin/integrations/stripe'
                  element={<StripeHealth />}
                />
                <Route
                  path='/dashboard/admin/integrations/google-analytics'
                  element={<GoogleAnalyticsPage />}
                />
                <Route
                  path='/dashboard/admin/sentry-test'
                  element={
                    <Navigate
                      to='/dashboard/admin/integrations/sentry'
                      replace
                    />
                  }
                />
                <Route
                  path='/dashboard/admin/audit-logs'
                  element={<AuditLogPage />}
                />
                <Route
                  path='/dashboard/admin/classes'
                  element={
                    <Navigate
                      to='/dashboard/admin/user-accounts/schools'
                      replace
                    />
                  }
                />
                <Route
                  path='/dashboard/admin/classes/:classId'
                  element={<AdminClassDetailPage />}
                />
                <Route
                  path='/dashboard/admin/profile'
                  element={<AdminProfilePage />}
                />
                <Route
                  path='/dashboard/admin/law-enforcement-reports'
                  element={<LawEnforcementReportPage />}
                />
                <Route
                  path='/dashboard/admin/maintenance'
                  element={<MaintenancePage />}
                />
                <Route
                  path='/dashboard/admin/announcements'
                  element={<AnnouncementManagementPage />}
                />
                <Route
                  path='/dashboard/admin/our-blogs'
                  element={<OurBlogsPage />}
                />
                <Route
                  path='/dashboard/admin/notification-test'
                  element={<NotificationTestPage />}
                />
                <Route
                  path='/dashboard/admin/cloud-functions'
                  element={<CloudFunctionsTestIndex />}
                />
                <Route
                  path='/dashboard/admin/cloud-functions/:functionId'
                  element={<CloudFunctionTestPage />}
                />
                <Route
                  path='/dashboard/admin/webhooks'
                  element={<WebhooksPage />}
                />
              </Route>
            </Route>

            {/* Parent Dashboard Routes */}
            <Route element={<RoleRoutes allowedRoles={[ROLES.PARENT]} />}>
              <Route element={<ParentDashboardLayout />}>
                <Route path='/dashboard/parent' element={<ParentEmptyPage />} />
                <Route
                  path='/dashboard/parent/add-child'
                  element={<AddAnotherChild />}
                />
                <Route
                  path='/dashboard/parent/profile'
                  element={<ParentMyProfilePage />}
                />
                <Route
                  path='/dashboard/parent/teaching-resources'
                  element={<ParentResourcePage />}
                />
                <Route
                  path='/dashboard/parent/help'
                  element={<ParentHelpPage />}
                />
                <Route
                  path='/dashboard/parent/manage-lessons/:childId'
                  element={<ManageChildLesson />}
                />
                <Route
                  path='/dashboard/parent/achievements'
                  element={<Achivements />}
                />
                <Route
                  path='/dashboard/parent/impact-report'
                  element={<ImpactReport />}
                />
                <Route
                  path='/dashboard/parent/family-talks'
                  element={<FamilyTalksPage />}
                />
                <Route
                  path='/dashboard/parent/contact'
                  element={<ParentContactPage />}
                />
                {/* <Route
                  path='/dashboard/parent/new-page'
                  element={<ParentEmptyPage />}
                /> */}
              </Route>
              <Route
                path='/dashboard/parent/profiles'
                element={<ParentSelectProfilePage />}
              />
              <Route
                path='/dashboard/parent/parent-onboarding'
                element={<ParentOnboarding />}
              />
            </Route>

            {/* Teacher Routes */}
            <Route element={<RoleRoutes allowedRoles={[ROLES.TEACHER]} />}>
              <Route element={<TeacherDashboardLayout />}>
                <Route
                  path='/dashboard/teacher'
                  element={<Navigate to='/dashboard/teacher/classroom' />}
                />
                <Route
                  path='/dashboard/teacher/classroom'
                  element={<TeacherClassroomPage />}
                />
                <Route
                  path='/dashboard/teacher/access-code'
                  element={<TeacherAccessCodePage />}
                />
                <Route
                  path='/dashboard/teacher/planner'
                  element={<TeacherPlannerPage />}
                />
                <Route
                  path='/dashboard/teacher/assignments'
                  element={<TeacherAssignmentsPage />}
                />
                <Route
                  path='/dashboard/teacher/quizzes'
                  element={<TeacherQuizzesPage />}
                />
                <Route
                  path='/dashboard/teacher/study-resources'
                  element={<TeacherResourcePage />}
                />
                <Route
                  path='/dashboard/teacher/analytics'
                  element={<TeacherAnalyticsPage />}
                />
                <Route
                  path='/dashboard/teacher/community'
                  element={<TeacherCommunityPage />}
                />
                <Route
                  path='/dashboard/teacher/profile'
                  element={<TeacherProfilePage />}
                />
                <Route
                  path='/dashboard/teacher/help'
                  element={<TeacherHelpPage />}
                />
                <Route
                  path='/dashboard/teacher/contact'
                  element={<TeacherContactPage />}
                />
              </Route>
            </Route>
            {/* Student Dashboard Routes */}
            <Route element={<RoleRoutes allowedRoles={[ROLES.STUDENT]} />}>
              <Route
                path='/dashboard/student/game/mission/:missionId'
                element={<MissionGamePage />}
              />

              <Route element={<StudentDashboardLayout />}>
                <Route
                  path='/dashboard/student'
                  element={<Navigate to='/dashboard/student/missions' />}
                />
                <Route
                  path='/dashboard/student/leaderboard'
                  element={<StudentLeaderboardPage />}
                />
                <Route
                  path='/dashboard/student/digi-shop'
                  element={<StudentDigiShopPage />}
                />
                <Route
                  path='/dashboard/student/missions'
                  element={<StudentMissionPage />}
                />
                <Route
                  path='/dashboard/student/quizzes'
                  element={<StudentQuizzesPage />}
                />
                <Route
                  path='/dashboard/student/notes'
                  element={<StudentNotesPage />}
                />
                <Route
                  path='/dashboard/student/profile'
                  element={<StudentProfilePage />}
                />
                <Route
                  path='/dashboard/student/support-chat'
                  element={<StudentSupportChatPage />}
                />
                <Route
                  path='/dashboard/student/my-protection-plan'
                  element={<StudentMyProtectionPlan />}
                />
                <Route
                  path='/dashboard/student/study-guide'
                  element={<StudyGuidePage />}
                />
                <Route
                  path='/dashboard/student/help'
                  element={<StudentHelpPage />}
                />
                <Route
                  path='/dashboard/student/quiz/:quizId'
                  element={<StudentQuizPage />}
                />
                <Route
                  path='/dashboard/student/contact'
                  element={<StudentContactPage />}
                />
                <Route
                  path='/dashboard/student/achievements'
                  element={<StudentAchievements />}
                />
              </Route>
            </Route>

            {/* Child Dashboard Routes */}
            <Route
              element={
                <RoleRoutes allowedRoles={[ROLES.CHILD, ROLES.PARENT]} />
              }
            >
              <Route
                path='/dashboard/child/game/mission/:missionId'
                element={<MissionGamePage />}
              />

              <Route element={<ChildDashboardLayout />}>
                <Route
                  path='/dashboard/child'
                  element={<Navigate to='/dashboard/child/missions' />}
                />
                <Route
                  path='/dashboard/child/quiz/:quizId'
                  element={<StudentQuizPage />}
                />

                <Route
                  path='/dashboard/child/missions'
                  element={<ChildMissionPage />}
                />
                <Route
                  path='/dashboard/child/leaderboard'
                  element={<ChildLeaderboardPage />}
                />
                <Route
                  path='/dashboard/child/digi-shop'
                  element={<ChildDigiShopPage />}
                />
                <Route
                  path='/dashboard/child/quizzes'
                  element={<ChildQuizPage />}
                />
                <Route
                  path='/dashboard/child/notes'
                  element={<ChildNotesPage />}
                />

                <Route
                  path='/dashboard/child/assignments'
                  element={<ChildAssignmentsPage />}
                />
                <Route
                  path='/dashboard/child/profile'
                  element={<ChildProfilePage />}
                />
                <Route
                  path='/dashboard/child/support-chat'
                  element={<ChildSupportChatPage />}
                />
                <Route
                  path='/dashboard/child/my-protection-plan'
                  element={<ChildMyProtectionPlanPlage />}
                />
                <Route
                  path='/dashboard/child/help'
                  element={<ChildHelpPage />}
                />
                <Route
                  path='/dashboard/child/achievements'
                  element={<ChildAchievements />}
                />
              </Route>
            </Route>

            {/* Minor Onboarding Routes */}
            <Route
              element={
                <RoleRoutes
                  allowedRoles={[
                    ROLES.STUDENT,
                    ROLES.CHILD,
                    ROLES.ADMIN,
                    ROLES.PARENT
                  ]}
                />
              }
            >
              <Route element={<OnboardingLayout />}>
                <Route
                  path='/onboarding/minor/role-select'
                  element={<RoleSelectPage />}
                />
                <Route
                  path='/onboarding/minor/plan-options'
                  element={<MinorPlanOptions />}
                />
                <Route path='/onboarding/payment' element={<PaymentPage />} />
                <Route
                  path='/onboarding/payment/success'
                  element={<PaymentSuccessPage />}
                />
                <Route
                  path='/onboarding/parent/child-account-type'
                  element={<ChildAccountTypePage />}
                />
                <Route
                  path='/onboarding/parent/create-child-account'
                  element={<CreateChildAccountPage />}
                />
                <Route
                  path='/onboarding/parent/family-plan-setup'
                  element={<FamilyPlanSetupPage />}
                />
                <Route
                  path='/onboarding/parent/children-account-details'
                  element={<ChildAccountSetup />}
                />
                <Route
                  path='/onboarding/parent/parent-setup-summary'
                  element={<ParentSetupSummaryPage />}
                />
                <Route
                  path='/onboarding/parent/edit-profiles'
                  element={<EditProfilesPage />}
                />
              </Route>
            </Route>

            {/* School Admin Routes */}
            <Route element={<RoleRoutes allowedRoles={[ROLES.SCHOOL_ADMIN]} />}>
              <Route
                path='/onboarding/educator/account-setup'
                element={<EducatorAccountSetup />}
              />
              <Route
                path='/school-plan-options'
                element={<SchoolPlanOptionsPage />}
              />
              <Route path='/success-payment' element={<SuccessPayment />} />
              <Route element={<EducatorDashboardLayout />}>
                <Route
                  path='/dashboard/educator'
                  element={<EducatorDashboardPage />}
                />
                <Route
                  path='/dashboard/educator/profile'
                  element={<EducatorProfilePage />}
                />
                <Route
                  path='/dashboard/educator/classrooms'
                  element={<EducatorClassroomPage />}
                />
                <Route
                  path='/dashboard/educator/school-users'
                  element={<SchoolUsers />}
                />
                <Route
                  path='/dashboard/educator/school-details'
                  element={<EducatorSchoolDetailsPage />}
                />
                <Route
                  path='/dashboard/educator/billing'
                  element={<EducatorBillingPage />}
                />
                <Route
                  path='/dashboard/educator/lessons'
                  element={<EducatoreLessonPage />}
                />
                <Route
                  path='/dashboard/educator/help'
                  element={<EducatorHelpPage />}
                />
                <Route
                  path='/dashboard/educator/contact'
                  element={<EducatorContactPage />}
                />
              </Route>
            </Route>
          </Route>

          <Route path='*' element={<NotFoundPage />} />
        </Routes>
      </SuspenseWrapper>
    </BrowserRouter>
  )
}

export default AppRoutes
